/* Home announcements (sections/home-announcements.liquid)
 *
 * Autoplay is driven by the active tab's fill animation: when the bar
 * finishes (animationend on ha-fill), the next card shows. Hover and
 * focus-within pause the animation from CSS, so the visible progress
 * and the actual timing can never drift apart. No animation (reduced
 * motion, autoplay off, <2 items) means no auto-advance.
 *
 * The Pause/Play button is the WCAG 2.2.2 control: hover-pause alone
 * doesn't exist on touch screens.
 */
(() => {
  if (customElements.get('home-announcements')) return;

  class HomeAnnouncements extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-ha-card]'));
      this.tabs = Array.from(this.querySelectorAll('[data-ha-tab]'));
      this.counter = this.querySelector('[data-ha-counter]');
      this.stage = this.querySelector('[data-ha-stage]');
      this.toggle = this.querySelector('[data-ha-toggle]');
      this.index = 0;

      if (this.cards.length < 2) {
        this.classList.remove('is-playing');
        return;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.remove('is-playing');
        if (this.toggle) this.toggle.hidden = true;
      }

      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          if (this.isRunning()) this.pause();
          else this.play();
        });
      }

      this.addEventListener('animationend', (e) => {
        if (e.animationName === 'ha-fill') {
          this.show((this.index + 1) % this.cards.length);
        }
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.stop();
          this.show(Number(tab.dataset.index));
        });
      });

      this.stage.addEventListener('touchstart', (e) => {
        this.touchX = e.touches[0].clientX;
        this.touchY = e.touches[0].clientY;
      }, { passive: true });
      this.stage.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - this.touchX;
        const dy = e.changedTouches[0].clientY - this.touchY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.stop();
          const step = dx < 0 ? 1 : -1;
          this.show((this.index + step + this.cards.length) % this.cards.length);
        }
      }, { passive: true });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          if (!this.contains(e.target)) return;
          const card = e.target.closest('[data-ha-card]');
          if (!card) return;
          this.classList.add('is-paused');
          this.show(Number(card.dataset.index));
        });
        document.addEventListener('shopify:block:deselect', (e) => {
          if (this.contains(e.target)) this.classList.remove('is-paused');
        });
      }
    }

    isRunning() {
      return this.classList.contains('is-playing') && !this.classList.contains('is-paused');
    }

    pause() {
      this.classList.add('is-paused');
      this.syncToggle();
    }

    play() {
      this.classList.remove('is-paused');
      if (!this.classList.contains('is-playing')) {
        this.classList.add('is-playing');
        this.show(this.index);
      }
      this.syncToggle();
    }

    /* A tab click or swipe stops autoplay; the Play button resumes it. */
    stop() {
      this.classList.remove('is-playing');
      this.syncToggle();
    }

    syncToggle() {
      if (!this.toggle) return;
      const running = this.isRunning();
      this.toggle.querySelector('[data-ha-toggle-text]').textContent = running ? 'Pause' : 'Play';
      this.toggle.querySelector('[data-ha-toggle-icon]').textContent = running ? '❚❚' : '▶';
      this.toggle.setAttribute('aria-label', `${running ? 'Pause' : 'Play'} rotating announcements`);
    }

    show(i) {
      this.index = i;
      this.cards.forEach((card, n) => {
        const active = n === i;
        card.classList.toggle('is-active', active);
        if (active) {
          card.removeAttribute('aria-hidden');
          card.removeAttribute('tabindex');
        } else {
          card.setAttribute('aria-hidden', 'true');
          card.setAttribute('tabindex', '-1');
        }
      });
      this.tabs.forEach((tab, n) => {
        tab.classList.toggle('is-active', n === i);
        tab.classList.toggle('is-past', n < i);
        if (n === i) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
        if (n === i) {
          /* Restart the fill even when the class set didn't change
             (e.g. re-selecting the current block in the editor). */
          const fill = tab.querySelector('[data-ha-fill]');
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      });
      if (this.counter) {
        this.counter.textContent = `${i + 1} of ${this.cards.length}`;
      }
    }
  }

  customElements.define('home-announcements', HomeAnnouncements);
})();
